import type { OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import type { FileId } from '@freelancer/file-storage';
import { FileStorage, StorageBucket } from '@freelancer/file-storage';
import { isImageFile } from '@freelancer/ui';
import { FileDisplaySize } from '@freelancer/ui/file-display';
import { FileSelectComponent } from '@freelancer/ui/file-select';
import { VerticalAlignment } from '@freelancer/ui/grid';
import { ListItemPadding } from '@freelancer/ui/list-item';
import { ProgressSize } from '@freelancer/ui/progress-bar';
import { isDefined, isFormControl } from '@freelancer/utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import type { Observable, Subscription } from 'rxjs';
import { of, combineLatest, firstValueFrom } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';

enum UploadStatus {
  FAILED = 'failed',
  IN_PROGRESS = 'in_progress',
  SUCCESS = 'success',
}
interface UploadedFile {
  readonly file: File;
  readonly previewUrl$: Observable<string | null>;
  readonly status$: Observable<UploadStatus>;
  readonly uploadProgress$: Observable<number>;
  readonly id$: Observable<FileId>;
}

@UntilDestroy({ className: 'FileUploadComponent' })
@Component({
  standalone: false,
  selector: 'fl-file-upload',
  template: `
    @if (fileUploadControl; as control) {
      @if (isFormControl(control)) {
        <fl-file-select
          #fileSelect
          class="FileUpload-uploadArea mb-xsmall"
          flTrackingLabel="EnterpriseFileUpload"
          i18n-note="File upload types"
          note="Upload either JPG, JPEG, PNG, GIF, BMP or SVG files (5MB max)"
          [control]="control"
          [alignment]="'left'"
        >
        </fl-file-select>
      }
      @if (uploadedFile) {
        <fl-list [bottomBorder]="true">
          <fl-list-item [padding]="ListItemPadding.MID">
            <fl-grid [vAlign]="VerticalAlignment.VERTICAL_CENTER">
              <fl-col
                class="FileUpload-fileName"
                [col]="6"
              >
                @if (uploadedFile.previewUrl$ | flAsync; as uploadedFile) {
                  <fl-file-display
                    class="mr-small"
                    alt="file preview"
                    i18n-alt="File preview alt text"
                    [size]="FileDisplaySize.MID"
                    [src]="uploadedFile"
                  ></fl-file-display>
                }
                <p class="text-body-16 FileUpload-fileName-text">
                  {{ uploadedFile.file.name }}
                </p>
              </fl-col>
              <fl-col [col]="3">
                <p class="text-body-16">
                  {{ uploadedFile.file.size | fileSize }}
                </p>
              </fl-col>
              @if (uploadedFile.status$ | flAsync; as status) {
                <fl-col
                  [col]="3"
                  [flexContainer]="true"
                >
                  @if (status === UploadStatus.IN_PROGRESS) {
                    @if (
                      uploadedFile.uploadProgress$ | flAsync;
                      as uploadProgress
                    ) {
                      <fl-progress-bar
                        class="mr-small"
                        label="File uploading"
                        i18n-label="file upload progress bar's label"
                        [progressPercentage]="uploadProgress"
                        [size]="ProgressSize.LARGE"
                      ></fl-progress-bar>
                    }
                  }
                  @if (status === UploadStatus.FAILED) {
                    <p
                      class="text-error text-body-16"
                      i18n="unsuccessful enterprise file upload"
                    >
                      Upload Failed. Please try again
                    </p>
                  }
                </fl-col>
              }
            </fl-grid>
          </fl-list-item>
        </fl-list>
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./file-upload.component.scss'],
})
export class FileUploadComponent implements OnInit, OnDestroy {
  private fileStorage = inject(FileStorage);

  FileDisplaySize = FileDisplaySize;
  isFormControl = isFormControl;
  ListItemPadding = ListItemPadding;
  ProgressSize = ProgressSize;
  UploadStatus = UploadStatus;
  VerticalAlignment = VerticalAlignment;

  @Input() fileUploadControl: FormControl<readonly File[]>;
  @Input() storageBucket: StorageBucket;
  @Output() onUploadSuccess = new EventEmitter<string>();

  uploadedFile?: UploadedFile;
  private fileUploadSubscription?: Subscription;

  @ViewChild('fileSelect')
  fileSelect: FileSelectComponent;

  ngOnInit(): void {
    this.fileUploadSubscription = this.fileUploadControl.valueChanges
      .pipe(
        filter(isDefined),
        switchMap((files: readonly File[]) => files),
      )
      .subscribe(file => {
        if (this.fileUploadControl.valid) {
          this.uploadFile(file);
        }
      });
  }

  private uploadFile(file: File): void {
    const previewUrl$ = isImageFile(file.name)
      ? this.fileSelect.readFileAsDataURL(file)
      : of(null);
    const uploadTask = this.fileStorage.upload(this.storageBucket, file);
    const uploadProgress$ = uploadTask.percentageChanges();
    const id$ = uploadTask.changes().pipe(map(upload => upload.metadata.id));
    const status$ = combineLatest([uploadTask.changes(), uploadProgress$]).pipe(
      map(([upload, progressRate]) =>
        upload.state === 'error'
          ? UploadStatus.FAILED
          : progressRate === 100
            ? UploadStatus.SUCCESS
            : UploadStatus.IN_PROGRESS,
      ),
    );
    this.uploadedFile = { file, previewUrl$, uploadProgress$, status$, id$ };

    firstValueFrom(
      combineLatest([
        uploadTask.completes(),
        id$.pipe(distinctUntilChanged()),
      ]).pipe(untilDestroyed(this)),
    ).then(([taskEvent, id]) => {
      if (taskEvent.state === 'success' && id !== undefined) {
        this.onUploadSuccess.emit(id);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.fileUploadSubscription) {
      this.fileUploadSubscription.unsubscribe();
    }
  }
}
